import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router";
import { AppShell } from "./AppShell.js";
import { queryClient } from "./components/shared/queryClient.js";
import { gatewayUrl, sentry } from "./components/utilities/frontEndConfig.js";
import "./index.scss";

const gatewayOrigin = new URL(gatewayUrl).origin;
const tracePropagationTargets = gatewayOrigin === window.location.origin ? [window.location.origin, /^\//] : [window.location.origin, gatewayOrigin, /^\//];
const sentryTags = {
  application: `zeus`,
  runtime: `browser`,
};

if (sentry.enabled && sentry.dsn !== ``) {
  Sentry.init({
    dsn: sentry.dsn,
    enabled: sentry.enabled,
    environment: sentry.environment,
    tracesSampleRate: sentry.tracesSampleRate,
    initialScope: {
      tags: sentryTags,
    },
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracePropagationTargets,
  });
}

/**
 * Root Zeus application component combining navigation and routed content.
 */
export const App = () => {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
};

const rootElement = document.getElementById(`root`);
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary fallback={<p className="u-text--muted">Zeus encountered an unexpected error.</p>}>
        <App />
      </Sentry.ErrorBoundary>
    </QueryClientProvider>,
  );
} else {
  console.error(`Root element not found`);
}
